import React, { memo, useCallback } from 'react';
import { ValidationError } from 'yup';

import { Button, Select, SpinnerOverlay } from '@components/common';
import { ETabsIds } from '@components/routes/AdminPanel/KpiAlertsManagement/AddNewKpiAlertRule/tabsIds';

import { useKpiAlertRecipients } from '@hooks/kpiAlerts/useKpiAlertRecipients';

import { isEmpty } from '@utils/common';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { IAddKpiNotificationRecipientProps, IKpiAlertSchema } from '../interfaces';

import { TUserRoles } from '@enums/common';
import { EAlarmManagementFields } from '@components/ModalComponents/AlarmManagementForm/enums';

import { filterValuesByKeys } from '../utils';

import { recipientFields, recipientSchema } from './schema';

export const ERecipientFields = {
	roles: 'roles',
	users: 'users',
} as const;

function AddKpiAlertRecipient({
	setActiveTab,
	formMethods,
	formState,
	values,
	negativeHandler,
	isEdit,
	positiveBtnText,
	negativeBtnText,
	positiveHandler,
}: IAddKpiNotificationRecipientProps) {
	const {
		setValue,
		setError,
		control,
		clearErrors,
		handleSubmit,
	} = formMethods;

	const { errors } = formState;

	const { usersOptions, rolesOptions, isLoading } = useKpiAlertRecipients();

	const onHandleNext = useCallback(async () => {
			if (isEdit) {
				return handleSubmit(positiveHandler)();
			}

			try {
				const recipientValues = filterValuesByKeys(values, Object.keys(ERecipientFields));
				await recipientSchema.validate(recipientValues, { abortEarly: false });

				setActiveTab(ETabsIds.channel);
			} catch (e) {
				const { message, path, inner } = e as ValidationError;

				if (path) {
					setError(path, { message: message as string });
				} else {
					inner.forEach((item: any) => {
						setError(item.path, { message: item.message });
					});
				}
			}
		},
		[
			isEdit,
			handleSubmit,
			positiveHandler,
			values,
			setActiveTab,
			setError,
		],
	);

	const onChangeRoles = (options: TSelectOption<TUserRoles>[]) => {
		setValue(ERecipientFields.roles, options, { shouldValidate: true, shouldDirty: true });

		if (!isEmpty(options)) {
			clearErrors(ERecipientFields.users);
		}
	};

	const onChangeUsers = (options: TSelectOption<string>[]) => {
		setValue(ERecipientFields.users, options, { shouldValidate: true, shouldDirty: true });

		if (!isEmpty(options)) {
			clearErrors(ERecipientFields.roles);
		}
	};

	const onBackHandler = useCallback(() => {
		negativeHandler(ETabsIds.schedule);
	}, [negativeHandler]);

	return (
		<div className='recipientContainer'>
			<div className='fieldLabel'>
				<span>Recipient</span>
				<span className='asterisk'>*</span>
			</div>
			<div className='display-flex flex-row flex-gap_30'>
				<div className='fieldsContainer'>
					<Select
						closeMenuOnSelect={false}
						control={control}
						errorMessage={errors?.roles?.message || errors?.users?.message}
						handleChange={onChangeRoles}
						isError={!!errors?.roles?.message}
						isMulti
						label='Roles'
						name={EAlarmManagementFields.roles}
						options={rolesOptions}
						placeholder='Choose roles'
						value={values[EAlarmManagementFields.roles]}
						width='100%'
					/>
				</div>
				<div className='fieldsContainer'>
					<Select
						closeMenuOnSelect={false}
						control={control}
						handleChange={onChangeUsers}
						isError={!!errors?.users?.message}
						isMulti
						label='Users'
						name={EAlarmManagementFields.users}
						options={usersOptions}
						placeholder='Choose users'
						value={values[EAlarmManagementFields.users]}
						width='100%'
					/>
				</div>
			</div>
			<div className='buttonsContainer'>
				<Button
					onClick={onBackHandler}
					text={negativeBtnText}
					withoutBackground
				/>
				<Button
					disabled={
						Object
							.keys(recipientFields)
							.some((key: string) => !!errors[key as keyof IKpiAlertSchema])
					}
					onClick={onHandleNext}
					text={positiveBtnText}
				/>
			</div>
			{isLoading && <SpinnerOverlay />}
		</div>
	);
}

export default memo(AddKpiAlertRecipient);
